import React, {type ReactElement, useEffect} from 'react'
import {Link as RouterLink} from 'react-router-dom'
import {Box} from '../../Box'
import {TextField} from '../../inputs/TextField'
import {Link} from '../../Link'
import {ToggleOpen} from '../../ToggleOpen'
import {Typography} from '../../Typography'
import {Table} from '../Table'
import {TableBody} from '../TableBody'
import {TableCell} from '../TableCell'
import {TableContainer} from '../TableContainer'
import {TablePagination} from '../TablePagination'
import {TableRow} from '../TableRow'
import {SortablePaginatedTableCell} from './SortablePaginatedTableCell'
import {SortablePaginatedTableHead} from './SortablePaginatedTableHead'
import {defaultSearch, type TableHeadCell} from './TableHeadCell'
import type {Order} from './types'
import {type Comparator, getComparator as getComparatorFunc, getRowsPerPageOptions, stableSort} from './utils'

interface SortablePaginatedTableProps<T extends {}> {
  dense?: boolean
  fixedLayout?: boolean
  getComparator?: Comparator<T>
  hasReachedPenultimatePage?: () => void
  initialOrder?: Order
  initialOrderByKey: keyof T
  initialRowsPerPage: number
  rows: T[]
  searchEnabled?: boolean
  subTitle?: string
  tableHeadCells: TableHeadCell<T>[]
  title?: string
  titleLinkUrl?: string
  rowLinkTo?: (row: T) => string
  maxTableWidth?: number | string
  rowsPerPageOptions?: number[]
  hiddenColumnIds?: Array<Extract<keyof T, string>>

  // Width controls
  minTableWidth?: number
  maxVisibleWidth?: number | string

  // When false, hides the fold/collapse control and always renders the table.
  collapsible?: boolean

  /** Horizontal alignment of the table within its container. Defaults to center. */
  contentAlign?: 'left' | 'center'
}

/**
 * When used together with a standalone title above the table,
 * do not supply a title to this component.
 */
export const SortablePaginatedTable = <T extends {id: string}>({
  dense = true,
  fixedLayout = false,
  getComparator = getComparatorFunc,
  hasReachedPenultimatePage,
  initialOrder = 'asc',
  initialOrderByKey,
  initialRowsPerPage = 5,
  rows,
  searchEnabled = true,
  subTitle,
  tableHeadCells,
  title,
  titleLinkUrl,
  rowLinkTo,
  maxTableWidth,
  rowsPerPageOptions: rowsPerPageOptionsOverride,
  hiddenColumnIds,
  minTableWidth,
  maxVisibleWidth,
  collapsible = true,
  contentAlign = 'center',
}: SortablePaginatedTableProps<T>): ReactElement<SortablePaginatedTableProps<T>> => {
  const [order, setOrder] = React.useState<Order>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof T>(initialOrderByKey)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(Math.min(initialRowsPerPage, rows.length))
  const [searchTerm, setSearchTerm] = React.useState<string>('')
  const [open, setOpen] = React.useState<boolean>(true)

  const useSizeControls = minTableWidth != null || maxVisibleWidth != null

  useEffect(() => {
    setRowsPerPage(Math.min(initialRowsPerPage, rows.length))
  }, [rows.length, initialRowsPerPage])

  const effectiveHeadCells =
    hiddenColumnIds && hiddenColumnIds.length > 0 ? tableHeadCells.filter((c) => !hiddenColumnIds.includes(c.id)) : tableHeadCells

  const firstExtraIndex = effectiveHeadCells.findIndex((c) => c.isExtra)

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof T) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    if ((newPage + 2) * rowsPerPage > rows.length) {
      hasReachedPenultimatePage?.()
    }
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentFirst = rowsPerPage * page
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(Math.floor(currentFirst / newRowsPerPage))
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage)

  const searchFilter = (row: T) =>
    effectiveHeadCells.some((cell) => (cell.search ? cell.search(row, cell.id, searchTerm) : defaultSearch(row, cell.id, searchTerm)))

  const filteredRows = stableSort(rows, getComparator(order, orderBy)).filter(searchFilter)

  const computedRowsPerPageOptions = getRowsPerPageOptions(initialRowsPerPage, filteredRows.length)

  const rowsPerPageOptions = rowsPerPageOptionsOverride ?? computedRowsPerPageOptions

  const showTable = !collapsible || open
  const hasHeaderContent = Boolean(title || subTitle)
  const tableContentAlign = contentAlign === 'left' ? 'flex-start' : 'center'

  return (
    <Box
      sx={
        useSizeControls
          ? {
              maxWidth: maxVisibleWidth,
              width: '100%',
              mx: 'auto',
            }
          : {
              maxWidth: fixedLayout ? maxTableWidth : undefined,
              width: fixedLayout && maxTableWidth ? 'max-content' : '100%',
              mx: fixedLayout && maxTableWidth ? 'auto' : undefined,
            }
      }
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Box>
          {title && (
            <Box>
              {titleLinkUrl ? (
                <Link component={RouterLink} to={titleLinkUrl}>
                  {title}
                </Link>
              ) : (
                <Typography variant="h3">{title}</Typography>
              )}
            </Box>
          )}
          {subTitle && (
            <Box mt={1}>
              <Typography>{subTitle}</Typography>
            </Box>
          )}
          {searchEnabled && (
            <Box mt={hasHeaderContent ? 2 : 0}>
              <TextField label="Search" value={searchTerm} onChange={setSearchTerm} />
            </Box>
          )}
        </Box>

        {collapsible ? <ToggleOpen open={open} onToggle={setOpen} /> : null}
      </Box>

      {showTable && (
        <Box
          sx={{
            overflowX: 'auto',
            display: 'flex',
            justifyContent: tableContentAlign,
          }}
        >
          <TableContainer sx={{width: contentAlign === 'left' ? '100%' : 'fit-content'}}>
            <Table
              sx={{
                minWidth: useSizeControls ? (minTableWidth ?? 750) : 750,
                width: useSizeControls ? 'max-content' : fixedLayout ? 'max-content' : '100%',
                tableLayout: fixedLayout ? 'fixed' : 'auto',
                borderCollapse: 'collapse',
                mx: contentAlign === 'left' ? 0 : 'auto',
              }}
              size={dense ? 'small' : 'medium'}
            >
              <SortablePaginatedTableHead headCells={effectiveHeadCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />

              <TableBody>
                {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow hover key={row.id}>
                    {effectiveHeadCells.map((cell, index) => (
                      <SortablePaginatedTableCell
                        dense={dense}
                        key={index}
                        first={index === 0}
                        maxWidth={cell.maxWidth}
                        minWidth={cell.minWidth}
                        tooltip={cell.cellTooltip?.(row)}
                        tooltipPlacement={cell.cellTooltipPlacement}
                        isExtra={cell.isExtra}
                        isExtraGroupStart={!!cell.isExtra && index === firstExtraIndex}
                        linkTo={cell.disableRowLink ? undefined : rowLinkTo?.(row)}
                      >
                        {cell.render(row)}
                      </SortablePaginatedTableCell>
                    ))}
                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{height: (dense ? 21 : 53) * emptyRows}}>
                    <TableCell colSpan={effectiveHeadCells.length} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {rowsPerPageOptions.length > 0 && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  )
}
